/**
 * DriverCasesPage ページ
 *
 * 陸送員専用ページ
 * 自分に割り当てられた案件の一覧表示と
 * ステータス変更操作
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Case } from '@/types';
import { SupabaseServiceFactory } from '@/services/supabase';
import { PageTitle } from '@/components/ui';
import { Alert } from '@/components/ui';
import TodaySummary from '@/components/driver/TodaySummary';
import NextCaseHighlight from '@/components/driver/NextCaseHighlight';
import logger from '@/utils/logger';

/**
 * DriverCasesPage
 *
 * 陸送員向けダッシュボード
 * - 本日のサマリー（統計情報）
 * - 次の作業ハイライト（最優先案件）
 * - ステータス変更操作（ワンタップ対応）
 */
export const DriverCasesPage: React.FC = () => {
  const [cases, setCases] = useState<Case[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alertState, setAlertState] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'info';
    message: string;
  }>({
    show: false,
    type: 'info',
    message: '',
  });

  // 案件一覧を読み込む
  const loadCases = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const caseService = SupabaseServiceFactory.getCaseService();
      const driverCases = await caseService.getDriverAssignedCases();

      setCases(driverCases);
      logger.info(`✅ ${driverCases.length}件の案件を読み込み`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '案件の読み込みに失敗しました';
      setError(errorMessage);
      logger.error('❌ 案件読み込みエラー:', err);

      setAlertState({
        show: true,
        type: 'error',
        message: `案件の読み込みに失敗しました: ${errorMessage}`,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 初期化時に案件を読み込む
  useEffect(() => {
    loadCases();
  }, [loadCases]);

  // ステータス更新時のハンドラ
  const handleStatusUpdated = useCallback((caseId: string, newStatus: Case['status']) => {
    setCases(prevCases => prevCases.map(c => (c.id === caseId ? { ...c, status: newStatus } : c)));

    setAlertState({
      show: true,
      type: 'success',
      message: 'ステータスを更新しました',
    });

    logger.info(`✅ 案件 ${caseId} のステータスを ${newStatus} に更新`);
  }, []);

  // エラーハンドラ
  const handleError = useCallback((caseId: string, error: Error) => {
    logger.error(`❌ 案件 ${caseId} の更新エラー:`, error);

    setAlertState({
      show: true,
      type: 'error',
      message: `ステータスの更新に失敗しました: ${error.message}`,
    });
  }, []);

  // ローディング状態
  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4'></div>
          <p className='text-gray-600 dark:text-gray-300'>案件を読み込み中...</p>
        </div>
      </div>
    );
  }

  // エラー状態
  if (error) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 p-4'>
        <PageTitle title='配車案件' subtitle='陸送員向け配車案件一覧' />
        <div className='mt-6'>
          <Alert variant='error' dismissible>
            {error}
          </Alert>
          <button
            onClick={loadCases}
            className='mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors'
          >
            再度読み込む
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900 p-4'>
      <PageTitle title='配車案件' subtitle='陸送員向け配車案件一覧' />

      {/* アラート表示 */}
      {alertState.show && (
        <div className='fixed top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4'>
          <Alert
            variant={alertState.type}
            dismissible
            onDismiss={() => setAlertState(prev => ({ ...prev, show: false }))}
          >
            {alertState.message}
          </Alert>
        </div>
      )}

      {/* ダッシュボード - 本日のサマリーと次の案件 */}
      {cases.length > 0 && (
        <div className='mt-6 space-y-6'>
          {/* 本日のサマリー */}
          <TodaySummary cases={cases} />

          {/* 次の案件ハイライト */}
          <NextCaseHighlight
            cases={cases}
            onStatusUpdated={handleStatusUpdated}
            onError={handleError}
          />
        </div>
      )}

      {/* 案件がない場合のみ空状態表示 */}
      {cases.length === 0 && (
        <div className='mt-8 bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center'>
          <p className='text-gray-600 dark:text-gray-400 text-lg'>
            現在、割り当てられている案件はありません
          </p>
          <button
            onClick={loadCases}
            className='mt-4 px-4 py-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium'
          >
            更新する
          </button>
        </div>
      )}

      {/* リロードボタン */}
      <div className='fixed bottom-6 right-6'>
        <button
          onClick={loadCases}
          disabled={isLoading}
          className='px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-800 shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          title='案件一覧を更新'
        >
          🔄 更新
        </button>
      </div>
    </div>
  );
};

export default DriverCasesPage;
